import React, { useState, useEffect, useRef, useCallback } from 'react';
import quizData from "../../data/home.json";
import questionsData from "../../data/questions.json";
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import articleData from "../../data/financeFacts.json";
import FactsSection from "../../components/FactsSection";
import AdComponent from "../../components/AdComponent";

const QuizGame = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const navigate = useNavigate();
    const [category, setCategory] = useState({});
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState({ correct: 0, incorrect: 0, total: 0 });
    const [answers, setAnswers] = useState({});
    const [usedLifelines, setUsedLifelines] = useState([]);
    const [timerPaused, setTimerPaused] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(60);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [eliminatedOptions, setEliminatedOptions] = useState({});
    const [audiencePoll, setAudiencePoll] = useState(null);
    const [toastMessage, setToastMessage] = useState('');
    const [questionAnswered, setQuestionAnswered] = useState(false);
    const [startTime, ] = useState(Date.now());
    const [coinsDeducted, setCoinsDeducted] = useState(false);
    const [article, setArticle] = useState({});

    const timerInterval = useRef(null);
    const totalQuestions = questions.length;
    const radius = 40;
    const circumference = radius * 2 * Math.PI;
    let options = {};

    useEffect(() => {
        if (!id || coinsDeducted) return;

        const category = quizData.find((c) => c.id === id);
        setCategory(category);
        const question = questionsData.filter((q) => q.category_id === id).sort(() => 0.5 - Math.random()).slice(0, 20);  
        if(question.length === 0){
            navigate(`/join-contest/${id}`, { state: 'No questions available for this quiz.' });
        } else if(user?.coins < category?.entry_fee){
            navigate(`/join-contest/${id}`, { state: 'You do not have enough coins to start this quiz.' });
        }
        setQuestions(question);
        const article = articleData?.PAGE2 || {};
        setArticle(article);
    
        dispatch({ type: "UPDATE_USER", payload: { coins: user?.coins - category?.entry_fee } });
        setCoinsDeducted(true);
    }, [id, dispatch, navigate, coinsDeducted, user?.coins]);

    // Load lifelines from localStorage on component mount
    useEffect(() => {
        const savedLifelines = localStorage.getItem(`quiz_used_lifelines_${category.id}`);
        if (savedLifelines) {
            setUsedLifelines(JSON.parse(savedLifelines));
        }
    }, [category.id]);

    // Save lifelines to localStorage
    useEffect(() => {
        localStorage.setItem(`quiz_used_lifelines_${category.id}`, JSON.stringify(usedLifelines));
    }, [usedLifelines, category.id]);
    
    // Auto-submit quiz when time runs out
    const autoSubmitQuiz = useCallback(() => {
        clearInterval(timerInterval.current);
        setShowResults(true);
        localStorage.removeItem(`quiz_used_lifelines_${category.id}`);
    }, [timerInterval, category.id]);

    // Timer functionality
    useEffect(() => {
        timerInterval.current = setInterval(() => {
            if (!timerPaused && timeRemaining > 0) {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        autoSubmitQuiz();
                        return 0;
                    }
                    return prev - 1;
                });
            }
        }, 1000);

        return () => clearInterval(timerInterval.current);
    }, [timerPaused, timeRemaining, autoSubmitQuiz]);

    // Calculate timer progress
    const timerProgress = (timeRemaining / 60) * circumference;
    const strokeOffset = circumference - timerProgress;

    // Show toast message
    const showToast = (message) => {
        setToastMessage(message);
        setTimeout(() => setToastMessage(''), 3000);
    };

    // Lifeline: 50-50
    const use5050 = () => {
        if (usedLifelines.includes('5050')) {
            showToast("50:50 lifeline already used!");
            return;
        }

        const currentQuestion = questions[currentIndex];
        const correctAnswer = currentQuestion.correct_answer;
        const options = ['a', 'b', 'c', 'd'];
        const incorrectOptions = options.filter(opt => opt !== correctAnswer);
        
        // Randomly select 2 incorrect options to eliminate
        const shuffled = incorrectOptions.sort(() => 0.5 - Math.random());
        const toEliminate = shuffled.slice(0, 2);
        
        setEliminatedOptions(prev => ({
            ...prev,
            [currentIndex]: [...(prev[currentIndex] || []), ...toEliminate]
        }));
        
        setUsedLifelines(prev => [...prev, '5050']);
        showToast("50:50 used! Two incorrect answers eliminated.");
    };

    // Lifeline: Audience Poll
    const useAudiencePoll = () => {
        if (usedLifelines.includes('audience')) {
            showToast("Audience Poll lifeline already used!");
            return;
        }

        const currentQuestion = questions[currentIndex];
        const correctAnswer = currentQuestion.correct_answer;
        
        // Generate realistic poll percentages
        const percentages = generateAudiencePoll(correctAnswer);
        setAudiencePoll(percentages);
        
        setUsedLifelines(prev => [...prev, 'audience']);
        showToast("Audience Poll results displayed!");
    };

    // Generate audience poll percentages
    const generateAudiencePoll = (correctAnswer) => {
        const options = ['a', 'b', 'c', 'd'];
        let percentages = {};
        
        // Correct answer gets 40-70% of votes
        const correctPercent = Math.floor(Math.random() * 31) + 40;
        percentages[correctAnswer] = correctPercent;
        
        // Distribute remaining percentage among other options
        let remaining = 100 - correctPercent;
        const otherOptions = options.filter(opt => opt !== correctAnswer);
        
        for (let i = 0; i < otherOptions.length; i++) {
            if (i === otherOptions.length - 1) {
                percentages[otherOptions[i]] = remaining;
            } else {
                const maxPercent = remaining - (otherOptions.length - i - 1);
                const percent = Math.floor(Math.random() * maxPercent) + 1;
                percentages[otherOptions[i]] = percent;
                remaining -= percent;
            }
        }
        
        return percentages;
    };

    // Lifeline: Flip Question
    const useFlipQuestion = () => {
        if (usedLifelines.includes('flip')) {
            showToast("Flip Question lifeline already used!");
            return;
        }
        if (currentIndex >= totalQuestions - 1) {
            showToast("Cannot flip the last question!");
            return;
        }

        nextQuestion();
        setUsedLifelines(prev => [...prev, 'flip']);
        showToast("Question flipped! Moving to next question.");
    };

    // Lifeline: Freeze Timer
    const useFreezeTimer = () => {
        if (usedLifelines.includes('timer')) {
            showToast("Freeze Timer lifeline already used!");
            return;
        }

        setTimerPaused(true);
        
        // Auto-resume after 30 seconds
        setTimeout(() => {
            setTimerPaused(false);
            showToast("Timer resumed!");
        }, 30000);
        
        setUsedLifelines(prev => [...prev, 'timer']);
        showToast("Timer frozen for 30 seconds!");
    };

    // Handle option selection
    const handleOptionClick = (optionValue) => {
        if (questionAnswered || eliminatedOptions[currentIndex]?.includes(optionValue)) return;

        setSelectedOption(optionValue);
        setQuestionAnswered(true);
        
        const currentQuestion = questions[currentIndex];
        const isCorrect = optionValue === currentQuestion.correct_answer;
        const points = isCorrect ? parseInt(currentQuestion.points) : 0;

        // Update score
        setScore(prev => ({
            correct: prev.correct + (isCorrect ? 1 : 0),
            incorrect: prev.incorrect + (isCorrect ? 0 : 1),
            total: prev.total + points
        }));

        // Store answer
        setAnswers(prev => ({
            ...prev,
            [currentIndex]: optionValue
        }));

        // Auto-advance after delay
        setTimeout(() => {
            if (currentIndex < totalQuestions - 1) {
                nextQuestion();
            } else {
                finishQuiz();
            }
        }, 1500);
    };

    // Move to next question
    const nextQuestion = () => {
        setCurrentIndex(prev => prev + 1);
        setSelectedOption(null);
        setQuestionAnswered(false);
        setAudiencePoll(null);
    };

    // Finish quiz
    const finishQuiz = () => {
        clearInterval(timerInterval.current);
        setShowResults(true);
        localStorage.removeItem(`quiz_used_lifelines_${category.id}`);
    };

    // Close quiz
    const closeQuiz = () => {
        if (window.confirm('Are you sure you want to close the quiz? Your progress will be lost.')) {
            localStorage.removeItem(`quiz_used_lifelines_${category.id}`);
            window.location.href = '/';
        }
    };

    // Get option class names
    const getOptionClassName = (optionKey) => {
        let className = 'quiz-option';
        
        if (eliminatedOptions[currentIndex]?.includes(optionKey)) {
            className += ' eliminated';
        }
        
        if (selectedOption === optionKey) {
            className += ' selected';
            if (questionAnswered && optionKey !== questions[currentIndex].correct_answer) {
                className += ' in-correct';
            }
        }
        
        if (questionAnswered && optionKey === questions[currentIndex].correct_answer) {
            className += ' correct';
        }
        
        return className;
    };

    if (showResults) {
        
        console.log("Quiz Results:", answers);
        let results = {
            category: category,
            total_questions: questions.length,
            correct: 0,
            wrong: 0,
            score: 0,
            total_points: 0,
            detailed_results: [],
            time_taken: startTime ? Math.floor(Date.now() / 1000 - startTime) : null,
            coins_deducted: category.entry_fee,
        };
    
        questions.forEach((question, index) => {
            const user_answer = answers[index] !== undefined ? answers[index] : null;
            const is_correct = user_answer === question.correct_answer;
            const points = is_correct ? parseInt(question.points) : 0;
        
            results.detailed_results.push({
                question_id: question.id,
                question: question.question,
                user_answer,
                correct_answer: question.correct_answer,
                is_correct,
                points,
                explanation: question.explanation,
            });
        
            if (is_correct) {
                results.correct++;
            } else {
                results.wrong++;
            }
        
            results.score += points;
            results.total_points += parseInt(question.points);
        });
    
        results.percentage =
        results.total_points > 0
            ? Math.round((results.score / results.total_points) * 10000) / 100
            : 0;
    
        console.log("Quiz Results:", results);
        navigate(`/quiz/result/${id}`, { state: results });
        // return (
        //     <div className="quiz-results">
        //         <div className="results-container">
        //             <h2>Quiz Completed!</h2>
        //             <div className="final-score">
        //                 <p>Your Final Score: <strong>{score.total}</strong></p>
        //                 <p>Correct Answers: <strong>{score.correct}</strong></p>
        //                 <p>Incorrect Answers: <strong>{score.incorrect}</strong></p>
        //                 <p>Total Questions: <strong>{totalQuestions}</strong></p>
        //             </div>
        //             <button onClick={() => window.location.reload()} className="restart-btn">
        //                 Play Again
        //             </button>
        //         </div>
        //     </div>
        // );
    }

    const currentQuestion = questions[currentIndex];
    if(currentQuestion){
        options = {
            a: currentQuestion.option_a,
            b: currentQuestion.option_b,
            c: currentQuestion.option_c,
            d: currentQuestion.option_d
        };
    }

    return (
        <>
            <div className="quiz-play-section">
                <div className="play-quiz-game">
                    {/* Header */}
                    <div className="play-quiz-header">
                        <h4>{category.topic_name}</h4>
                        <p>Play and Win {category.winner_coins}
                            <span className="coin">
                                <img src="/assets/images/dollar.png" alt="coin" />
                            </span>
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="play-quiz-stats">
                        <span className="align-items-center score-correct">{score.correct}</span>
                        <div className="quiz-timer">
                            <div className={`timer-circle ${timerPaused ? 'timer-paused' : ''}`}>
                                <svg className="progress-ring" width="90" height="90">
                                    <circle className="progress-ring__bg" cx="45" cy="45" r="40" />
                                    <circle className="progress-ring__circle" cx="45" cy="45" r="40" style={{
                                        strokeDasharray: `${circumference} ${circumference}`,
                                        strokeDashoffset: strokeOffset,
                                    }} />
                                </svg>
                                <div className="inner-circle">
                                    <span id="timer-value">{timeRemaining}</span>
                                </div>
                            </div>
                        </div>
                        <span className="score-wrong">{score.incorrect}</span>
                    </div>

                    {/* Question */}
                    <div className="play-quiz-box">
                        <div className="play-quiz active">
                            <p className="quiz-count">Question {currentIndex + 1} / {totalQuestions}</p>
                            <div className="flex-grow-1">
                                <h5 className="card-title">{currentQuestion?.question}</h5>
                                {currentQuestion?.question_image && (
                                    <img 
                                        src={`assets/images/questions/${currentQuestion.question_image}`} 
                                        alt="Question" 
                                        className="img-fluid mt-3" 
                                        style={{ maxHeight: '200px' }} 
                                    />
                                )}
                            </div>

                            {/* Audience Poll Results */}
                            {audiencePoll && (
                                <div className="audience-poll-result">
                                    <h6>📊 Audience Poll Results</h6>
                                    {Object.entries(audiencePoll).map(([option, percent]) => (
                                        <div key={option} className="poll-option">
                                            <span>Option {option.toUpperCase()}: {percent}%</span>
                                            <div className="poll-bar">
                                                <div 
                                                    className="poll-fill" 
                                                    style={{ width: `${percent}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            
                            <input type="hidden" name={`answers[${currentIndex}]`} id={`answer_${currentIndex}`} />
                            <input type="hidden" name={`qus[${currentIndex}]`} id={`qus_${currentIndex}`} />

                            {/* Options */}
                            <div className="quiz-options">
                                {Object.entries(options).map(([key, option]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={getOptionClassName(key)}
                                        onClick={() => handleOptionClick(key)}
                                        disabled={questionAnswered || eliminatedOptions[currentIndex]?.includes(key)}
                                    >
                                        {/* <strong>{key.toUpperCase()}.</strong>  */}
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Lifelines */}
                    <div className="lifeline-section">
                        <div className="lifeline-buttons">
                            <div 
                                className={`lifeline-btn ${usedLifelines.includes('5050') ? 'used' : ''}`}
                                onClick={use5050}
                            >
                                <div className="lifeline-icon">
                                    <img src="/assets/images/fifty-50.svg" alt="50:50" />
                                </div>
                                <span className="lifeline-label">50:50</span>
                            </div>

                            <div 
                                className={`lifeline-btn ${usedLifelines.includes('audience') ? 'used' : ''}`}
                                onClick={useAudiencePoll}
                            >
                                <div className="lifeline-icon">
                                    <img src="/assets/images/audience-poll.svg" alt="Audience Poll" />
                                </div>
                                <span className="lifeline-label">Audience Poll</span>
                            </div>

                            <div 
                                className={`lifeline-btn ${usedLifelines.includes('timer') ? 'used' : ''}`}
                                onClick={useFreezeTimer}
                            >
                                <div className="lifeline-icon">
                                    <img src="/assets/images/freeze-timer.svg" alt="Freeze Timer" />
                                </div>
                                <span className="lifeline-label">Freeze Timer</span>
                            </div>

                            <div 
                                className={`lifeline-btn ${usedLifelines.includes('flip') ? 'used' : ''}`}
                                onClick={useFlipQuestion}
                            >
                                <div className="lifeline-icon">
                                    <img src="/assets/images/flip-question.svg" alt="Flip Question" />
                                </div>
                                <span className="lifeline-label">Flip Question</span>
                            </div>
                        </div>
                    </div>

                    {/* Close Button */}
                    {/* <div className="quiz-close-section">
                        <button type="button" className="close-quiz-btn" onClick={closeQuiz}>
                            CLOSE
                        </button>
                    </div> */}

                    {/* Score Display */}
                    <div className="quiz-score-display">
                        <p className="quiz-score pb-3">Your score: <strong>{score.total}</strong></p>
                    </div>
                </div>
                <AdComponent/>
                {/* Toast Notification */}
                {toastMessage && (
                    <div className="lifeline-toast" style={{ transform: 'translateX(0)' }}>
                        <div className="toast-content">
                            <span>✨</span>
                            <span>{toastMessage}</span>
                        </div>
                    </div>
                )}

                <style>{`
                    .quiz-timer {
                        position: relative;
                    }

                    .timer-circle {
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .progress-ring {
                        transform: rotate(-90deg);
                    }

                    .progress-ring__bg {
                        stroke: #34495e;
                        stroke-width: 6;
                        fill: transparent;
                    }

                    .progress-ring__circle {
                        stroke: var(--first-color);
                        stroke-width: 6;
                        fill: transparent;
                        transition: stroke-dashoffset 0.35s;
                        stroke-dasharray: 0;
                        stroke-dashoffset: 0;
                    }

                    .inner-circle {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                    }

                    #timer-value {
                        font-size: 24px;
                        font-weight: bold;
                        color: var(--first-color);
                    }

                    /* Timer Paused State - FIXED */
                    .timer-paused #timer-value {
                        color: #e74c3c !important;
                        animation: pulse 1s infinite;
                    }

                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.6; }
                        100% { opacity: 1; }
                    }

                    /* Quiz Question Styling - ADDED MISSING STYLES */
                    .quiz-count {
                        color: #bdc3c7;
                        text-align: center;
                        margin-bottom: 20px;
                        font-size: 14px;
                    }

                    .card-title {
                        color: white;
                        text-align: center;
                        font-size: 18px;
                        font-weight: 500;
                        margin-bottom: 25px;
                        line-height: 1.4;
                    }

                    .quiz-options {
                        display: grid;
                        gap: 12px;
                        margin-bottom: 30px;
                    }

                    .quiz-option {
                        background: var(--first-color);
                        color: white;
                        border: none;
                        border-radius: 10px;
                        padding: 10px 10px;
                        font-size: 14px;
                        font-weight: 500;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        border: 2px solid transparent;
                    }

                    .quiz-option:hover:not(.eliminated):not(:disabled) {
                        background: var(--first-color);
                        transform: translateY(-2px);
                        box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
                    }

                    .quiz-option.selected {
                        background: #27ae60;
                        border-color: #2ecc71;
                    }

                    .quiz-option.selected.in-correct {
                        background: #ff0000;
                        border-color: #ff0000;
                    }

                    /* FIXED Eliminated Options Styling */
                    .quiz-option.eliminated {
                        background: #7f8c8d !important;
                        color: #bdc3c7 !important;
                        opacity: 0.5;
                        cursor: not-allowed;
                        text-decoration: line-through;
                    }

                    .quiz-option.eliminated:hover {
                        background: #7f8c8d !important;
                        transform: none;
                        box-shadow: none;
                    }

                    /* Lifeline Section */
                    .lifeline-section {
                        margin: 30px 0;
                    }

                    .lifeline-buttons {
                        display: grid;
                        grid-template-columns: repeat(4, 1fr);
                        gap: 15px;
                        margin-bottom: 20px;
                    }

                    .lifeline-btn {
                        background: rgba(52, 73, 94, 0.8);
                        border: 2px solid #34495e;
                        border-radius: 12px;
                        padding: 15px 8px;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        color: white;
                    }

                    .lifeline-btn:hover:not(.used) {
                        background: rgba(52, 152, 219, 0.2);
                        border-color: var(--first-color);
                        transform: translateY(-3px);
                    }

                    .lifeline-btn.used {
                        background: rgba(127, 140, 141, 0.5);
                        border-color: #7f8c8d;
                        cursor: not-allowed;
                        opacity: 0.5;
                    }

                    .lifeline-btn.used:hover {
                        transform: none;
                    }

                    .lifeline-icon {
                        font-size: 24px;
                        margin-bottom: 8px;
                        height: 35px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .lifeline-icon img {
                        width: 30px;
                        height: 30px;
                        filter: brightness(0) invert(1);
                    }

                    .lifeline-btn.used .lifeline-icon img {
                        filter: brightness(0) invert(0.5);
                    }

                    .lifeline-text {
                        font-weight: bold;
                        font-size: 16px;
                    }

                    .lifeline-label {
                        font-size: 11px;
                        font-weight: 500;
                        display: block;
                        line-height: 1.2;
                    }

                    /* Close Button */
                    .quiz-close-section {
                        text-align: center;
                        margin: 20px 0;
                    }

                    .close-quiz-btn {
                        background: var(--first-color);
                        color: white;
                        border: none;
                        border-radius: 25px;
                        padding: 12px 40px;
                        font-size: 14px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                    }

                    .close-quiz-btn:hover {
                        background: var(--first-color);
                        transform: translateY(-2px);
                    }

                    /* Score Display */
                    .quiz-score-display {
                        text-align: center;
                        margin-top: 15px;
                    }

                    .quiz-score {
                        color: #bdc3c7;
                        font-size: 16px;
                        margin: 0;
                    }

                    .quiz-score strong {
                        color: white;
                        font-size: 18px;
                    }

                    /* Audience Poll Styling */
                    .audience-poll-result {
                        background: rgba(44, 62, 80, 0.9);
                        border: 2px solid var(--first-color);
                        border-radius: 15px;
                        padding: 20px;
                        margin: 20px 0;
                    }

                    .audience-poll-result h6 {
                        color: white;
                        text-align: center;
                        margin-bottom: 15px;
                    }

                    .poll-option span {
                        color: white;
                        font-weight: 500;
                        margin-bottom: 8px;
                        display: block;
                    }

                    .poll-bar {
                        height: 25px;
                        background: rgba(52, 73, 94, 0.7);
                        border-radius: 15px;
                        overflow: hidden;
                        margin-bottom: 12px;
                    }

                    .poll-fill {
                        height: 100%;
                        background: var(--first-color);
                        border-radius: 15px;
                        transition: width 1.5s ease;
                    }

                    /* Toast Notifications */
                    .lifeline-toast {
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: var(--first-color);
                        color: white;
                        padding: 15px 20px;
                        border-radius: 12px;
                        box-shadow: 0 8px 25px rgba(0,0,0,0.3);
                        z-index: 1000;
                        transform: translateX(100%);
                        transition: transform 0.3s ease;
                        max-width: 300px;
                    }

                    .toast-content {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    /* Responsive Design */
                    @media (max-width: 768px) {
                        .play-quiz-game {
                            margin: 10px;
                            padding: 0px;
                        }
                        
                        .lifeline-buttons {
                            grid-template-columns: repeat(4, 1fr);
                            gap: 10px;
                        }
                        
                        .lifeline-btn {
                            padding: 12px 6px;
                        }
                        
                        .lifeline-icon {
                            font-size: 20px;
                            height: 30px;
                        }
                        
                        .lifeline-label {
                            font-size: 10px;
                        }
                    }

                    /* Hide/Show Animations */
                    .play-quiz {
                        opacity: 0;
                        visibility: hidden;
                    }

                    .play-quiz.active {
                        opacity: 1;
                        visibility: visible;
                    }
                `}</style>
            </div>

            <section className="facts-section">
                <div className="container">
                    <div className="finance-fact">
                        <h2 className="fw-bold mb-4">{article?.title}</h2>
                    </div>
                    {
                        article?.content?.map((item, index) => (
                            <FactsSection key={index} item={item} />
                        ))
                    }
                    <div className="article-card">
                        <h2 className="article-card-title">{article?.action?.title}</h2>
                        
                        <p className="article-card-desc">
                            <strong className="desc-color">{article?.action?.title1}</strong> {article?.action?.desc1}
                        </p>
                        
                        <p className="article-card-desc">
                            <strong>{article?.action?.title2}</strong> {article?.action?.desc2}
                        </p>
                        
                        <p className="article-card-desc">
                            {
                                article?.action?.list.map((list, index) => (
                                    <><span key={index}>{list}</span><br /></>
                                ))
                            }
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
};

export default QuizGame;